import React, { useState } from "react";
import { Input } from "@showitinc/showit-wc";

import styles from "./SimplePaddingControl.module.css";
import ContextButton from "@/app2components/ContextMenus/ContextButton";

const getPaddingValuesFromCssString = (padding) => {
  const [top, right, bottom, left] = padding?.replaceAll("px", "").split(" ") ?? [0, 0, 0, 0];
  return { top, right, bottom, left };
};

const MAX_VERTICAL_PADDING = 34;
const MAX_HORIZONTAL_PADDING = 54;

const getClampedPaddingValues = (padding) => {
  const { top, right, bottom, left } = getPaddingValuesFromCssString(padding);
  return `${Math.min(top, MAX_VERTICAL_PADDING)}px ${Math.min(
    right,
    MAX_HORIZONTAL_PADDING
  )}px ${Math.min(bottom, MAX_VERTICAL_PADDING)}px ${Math.min(left, MAX_HORIZONTAL_PADDING)}px`;
};

/**
 * Signal's the parent menu holder to open if it exists
 * @param {Event} e
 */
const handleContextClick = (e) => {
  const target = e?.target;
  if (target instanceof HTMLElement) {
    target.dispatchEvent(
      new CustomEvent("si-context-click", {
        bubbles: true,
        detail: { target },
      })
    );
  }
};

const sharedInputProps = {
  hideLabel: true,
  longLabel: true,
  min: "0",
  max: "100",
  type: "number",
};

const getFormattedPaddingAsCssString = ({ top, right, bottom, left }) =>
  `${top}px ${right}px ${bottom}px ${left}px`;

export default function PaddingControl({
  value: padding,
  onSIChange,
  onSIBeforeChange,
  onSIInput,
  disabled,
}) {
  // State to track whether horizontal (left/right) and vertical (top/bottom) values are linked
  const paddingValues = getPaddingValuesFromCssString(padding);
  const [horizontalLinked, setHorizontalLinked] = useState(
    paddingValues.left === paddingValues.right
  );
  const [verticalLinked, setVerticalLinked] = useState(paddingValues.top === paddingValues.bottom);

  const onPaddingChange = (side, handler) => (e) => {
    // encapsulate changes so these aren't caught by a parent
    e.stopPropagation();
    const newPadValue = e.target.value;
    const newPadding = { ...paddingValues, [side]: newPadValue };

    // If horizontal values are linked, update both left and right
    if (horizontalLinked && (side === "left" || side === "right")) {
      newPadding.left = newPadValue;
      newPadding.right = newPadValue;
    }

    // If vertical values are linked, update both top and bottom
    if (verticalLinked && (side === "top" || side === "bottom")) {
      newPadding.top = newPadValue;
      newPadding.bottom = newPadValue;
    }

    handler({ detail: { value: getFormattedPaddingAsCssString(newPadding) } });
  };

  const handlers = {
    onSIChange,
    onSIBeforeChange,
    onSIInput,
  };

  const toggleHorizontalLink = () => {
    setHorizontalLinked(!horizontalLinked);
    const newPadding = { ...paddingValues, left: paddingValues.right };
    onSIInput({ detail: { value: getFormattedPaddingAsCssString(newPadding) } });
  };

  const toggleVerticalLink = () => {
    setVerticalLinked(!verticalLinked);
    const newPadding = { ...paddingValues, bottom: paddingValues.top };
    onSIInput({ detail: { value: getFormattedPaddingAsCssString(newPadding) } });
  };

  /**
   * Prepares si handlers
   * @param {"top" | "right" | "bottom" | "left"} side
   * @returns {{ [key: keyof handlers]: ((e: CustomEvent) => void | undefined)}}
   */
  const prepareHandlers = (side) =>
    Object.entries(handlers).reduce((result, [key, handler]) => {
      if (handler) {
        result[key] = onPaddingChange(side, handler);
      }

      return result;
    }, {});

  return (
    <div className={styles.container}>
      <div className={styles.menuContainer}>
        <p className={styles.title}>Padding</p>
        <div className={styles.menu}>
          <ContextButton onClick={handleContextClick} disabled={disabled} />
        </div>
      </div>
      <div className={styles.previewContainer}>
        {/* Top padding input */}
        <div className={`${styles.paddingInput} ${styles.topInput}`}>
          <Input
            {...prepareHandlers("top")}
            {...sharedInputProps}
            aria-label="Top padding"
            className={styles.input}
            disabled={disabled}
            value={paddingValues.top}
          />
        </div>

        {/* Right padding input */}
        <div className={`${styles.paddingInput} ${styles.rightInput}`}>
          <Input
            {...prepareHandlers("right")}
            {...sharedInputProps}
            aria-label="Right padding"
            className={styles.input}
            disabled={disabled}
            value={paddingValues.right}
          />
        </div>

        {/* Bottom padding input */}
        <div className={`${styles.paddingInput} ${styles.bottomInput}`}>
          <Input
            {...prepareHandlers("bottom")}
            {...sharedInputProps}
            aria-label="Bottom padding"
            className={styles.input}
            disabled={disabled}
            value={paddingValues.bottom}
          />
        </div>

        {/* Left padding input */}
        <div className={`${styles.paddingInput} ${styles.leftInput}`}>
          <Input
            {...prepareHandlers("left")}
            {...sharedInputProps}
            aria-label="Left padding"
            className={styles.input}
            disabled={disabled}
            value={paddingValues.left}
          />
        </div>

        {/* Horizontal link toggle */}
        <button
          type="button"
          className={`${styles.linkToggle} ${styles.horizontalLinkToggle} ${
            horizontalLinked ? styles.linked : ""
          }`}
          onClick={toggleHorizontalLink}
          disabled={disabled}
          aria-label={horizontalLinked ? "Unlink horizontal padding" : "Link horizontal padding"}
          title={horizontalLinked ? "Unlink left and right padding" : "Link left and right padding"}
        >
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {horizontalLinked ? (
              <>
                <path d="M8 12h8" />
                <path d="M8 12a4 4 0 1 1-4-4 4 4 0 0 1 4 4z" />
                <path d="M16 12a4 4 0 1 1 4 4 4 4 0 0 1-4-4z" />
              </>
            ) : (
              <>
                <path d="M8 12h8" />
                <path d="M8 12a4 4 0 1 1-4-4 4 4 0 0 1 4 4z" />
                <path d="M16 12a4 4 0 1 1 4 4 4 4 0 0 1-4-4z" />
                <path d="M3 3l18 18" />
              </>
            )}
          </svg>
        </button>

        {/* Vertical link toggle */}
        <button
          type="button"
          className={`${styles.linkToggle} ${styles.verticalLinkToggle} ${
            verticalLinked ? styles.linked : ""
          }`}
          onClick={toggleVerticalLink}
          disabled={disabled}
          aria-label={verticalLinked ? "Unlink vertical padding" : "Link vertical padding"}
          title={verticalLinked ? "Unlink top and bottom padding" : "Link top and bottom padding"}
        >
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {verticalLinked ? (
              <>
                <path d="M12 8v8" />
                <path d="M12 8a4 4 0 1 1-4-4 4 4 0 0 1 4 4z" />
                <path d="M12 16a4 4 0 1 1 4 4 4 4 0 0 1-4-4z" />
              </>
            ) : (
              <>
                <path d="M12 8v8" />
                <path d="M12 8a4 4 0 1 1-4-4 4 4 0 0 1 4 4z" />
                <path d="M12 16a4 4 0 1 1 4 4 4 4 0 0 1-4-4z" />
                <path d="M3 3l18 18" />
              </>
            )}
          </svg>
        </button>

        {/* Preview area */}
        <div className={styles.previewWrapper}>
          <div
            className={styles.previewOuter}
            style={{ padding: getClampedPaddingValues(padding) }}
          >
            <div className={styles.previewContent}>Text</div>
          </div>
        </div>

        {/* Labels */}
        <div className={styles.labelTop}>Top</div>
        <div className={styles.labelRight}>Right</div>
        <div className={styles.labelBottom}>Bottom</div>
        <div className={styles.labelLeft}>Left</div>
      </div>
    </div>
  );
}
